import { injectable, unmanaged } from 'inversify';
import type { BaseDocumentModel } from './models';
import type { Migration } from './interfaces';
import type { DocumentWithChildren } from '../types';

@injectable()
export abstract class BaseMigrationService<TModel extends BaseDocumentModel = BaseDocumentModel>
  implements Migration<TModel>
{
  #currentVersion: number;

  constructor(@unmanaged() currentVersion: number) {
    this.#currentVersion = currentVersion;
  }

  getModules(schemaVersion: number) {
    const modules = [];
    let v = schemaVersion;
    while (v < this.#currentVersion) {
      modules.push(this.load(++v));
    }
    return modules;
  }

  async apply(node: TModel, isNew = false): Promise<TModel> {
    if (isNew) {
      node.schemaVersion = this.#currentVersion;
      return node;
    }

    if (node.schemaVersion >= this.#currentVersion) {
      return node;
    }

    const modules = this.getModules(node.schemaVersion ?? 0);
    const funcs = await Promise.all(modules);

    if (!funcs) {
      return node;
    }

    const newModel = funcs.reduce((acc, func: unknown) => {
      if (typeof func !== 'function') {
        throw new TypeError(`Migration function wasn't found!`);
      }

      const data = func.call(this, acc);

      return { ...data };
    }, node);

    newModel.schemaVersion = this.#currentVersion;

    return newModel;
  }

  async applyTree(models: DocumentWithChildren<TModel>[], isNew = false) {
    type Model = DocumentWithChildren<TModel>;

    const iterate = async (item: Model): Promise<Model> => {
      const newItem = await this.apply(item, isNew);

      const { children } = item;

      return {
        ...newItem,
        children: children ? await Promise.all(children.map(child => iterate(child))) : undefined,
      };
    };

    return Promise.all(models.map(document => iterate(document)));
  }

  applyArray(models: TModel[], isNew = false) {
    return Promise.all(models.map(model => this.apply(model, isNew)));
  }

  get version() {
    return this.#currentVersion;
  }

  // eslint-disable-next-line class-methods-use-this
  load(version: number) {
    throw new Error(`Method not implemented v${version}`);
  }
}
